/**
 * FERP Branded ID Types - TypeScript Type Safety for Backend-Generated IDs
 *
 * Layer 3 of Multi-Layer ID Generation Enforcement
 *
 * ARCHITECTURE: Defense-in-depth following AUTOMATED_ENFORCEMENT_PATTERNS.md
 * - Layer 1: ESLint import restrictions (uuid/nanoid banned) ✅ ACTIVE
 * - Layer 2: Custom ESLint plugin (Date.now/Math.random detection) ✅ ACTIVE
 * - Layer 3: TypeScript branded types (prevent string→ID) ✅ THIS FILE
 * - Layer 4: Pre-commit hooks 🔄 NEXT
 * - Layer 5: CI/CD gates 🔄 NEXT
 *
 * PHILOSOPHY: "Make it impossible to do wrong"
 * - Branded types prevent arbitrary strings from being used as IDs
 * - Only backend-generated IDs can be assigned to BackendId type
 * - Type system enforces at compile time (no runtime cost)
 *
 * FERP Compliance:
 * - Life-critical system: Type safety prevents ID forgery
 * - Audit trail integrity: Only validated IDs in system
 * - Multi-tenant security: Type guarantees from backend
 *
 * @see https://github.com/microsoft/TypeScript/wiki/FAQ#what-is-a-branded-type
 */

/**
 * Base branded type for backend-generated IDs
 *
 * USAGE:
 * ```typescript
 * // ✅ CORRECT: From backend
 * const id: BackendId = await generateId(EntityType.WORKFLOW);
 *
 * // ❌ ERROR: Arbitrary string not allowed
 * const id: BackendId = "some-random-string"; // Type error!
 *
 * // ❌ ERROR: Date.now() not allowed
 * const id: BackendId = `workflow_${Date.now()}`; // Type error!
 * ```
 *
 * IMPLEMENTATION: Nominal typing via unique symbol
 * - Creates a distinct type that's not assignable from string
 * - Zero runtime overhead (erased during compilation)
 * - Compile-time safety only
 */
declare const BackendIdBrand: unique symbol;
export type BackendId = string & { readonly [BackendIdBrand]: true };

/**
 * Snowflake ID - 64-bit distributed ID from backend
 *
 * FORMAT: 11-character Base62 encoding
 * STRUCTURE: timestamp(41) + machine(10) + sequence(12) + sign(1)
 *
 * PROPERTIES:
 * - Time-sortable (timestamp prefix)
 * - Collision-resistant (4096 IDs/ms/machine)
 * - Globally unique (1024 machines supported)
 * - URL-safe (Base62: [0-9A-Za-z])
 *
 * EXAMPLE: "7jK9mNpQrSt"
 */
export type SnowflakeId = BackendId;

/**
 * Entity-specific ID types for additional type safety
 *
 * These are ALIASES to BackendId for documentation purposes
 * They do NOT provide additional type safety (intentional)
 *
 * RATIONALE: Same ID format across all entities (Snowflake)
 * - WorkflowId can be used where TemplateId expected (same format)
 * - Prevents overly restrictive typing
 * - Maintains backend Snowflake ID consistency
 */
export type WorkflowId = BackendId;
export type TemplateId = BackendId;
export type InstanceId = BackendId;
export type UserId = BackendId;
export type TenantId = string; // Note: TenantId is NOT backend-generated (config-driven)
export type EntityId = BackendId;
export type SessionId = BackendId;
export type ApprovalId = BackendId;
export type ExecutionId = BackendId;
export type TransitionId = BackendId;
export type ActionId = BackendId;
export type GuardId = BackendId;
export type ContextId = BackendId;
export type TaskId = BackendId;
export type SignalId = BackendId;
export type StateId = BackendId;
export type ActorId = BackendId;
export type PolicyId = BackendId;

/**
 * Type guard to validate BackendId format
 *
 * VALIDATION:
 * - Length: 11 characters
 * - Characters: Base62 [0-9A-Za-z]
 * - Pattern: Snowflake ID format
 *
 * USAGE:
 * ```typescript
 * const maybeId: string = response.id;
 * if (isBackendId(maybeId)) {
 *   const validId: BackendId = maybeId; // Type assertion safe
 * }
 * ```
 *
 * ⚠️ WARNING: This is a RUNTIME check
 * Use only when receiving IDs from external sources (API, localStorage, etc.)
 * Do NOT use for IDs from useGenerateId() (already typed correctly)
 */
export function isBackendId(value: unknown): value is BackendId {
  if (typeof value !== 'string') return false;

  // Snowflake ID format: 11 characters, Base62
  const snowflakePattern = /^[0-9A-Za-z]{11}$/;
  return snowflakePattern.test(value);
}

/**
 * Type assertion for BackendId (UNSAFE - use sparingly)
 *
 * SAFETY: This bypasses type safety. Use ONLY when:
 * 1. You've validated the ID with isBackendId()
 * 2. You're migrating existing code gradually
 * 3. You're receiving IDs from trusted backend response
 *
 * ⚠️ WARNING: This defeats the purpose of branded types
 * Prefer using useGenerateId() hook instead
 *
 * USAGE:
 * ```typescript
 * // ⚠️ UNSAFE: Only if you've validated
 * const id = asBackendId(trustedResponse.id);
 *
 * // ✅ SAFE: Validate first
 * if (isBackendId(response.id)) {
 *   const id = asBackendId(response.id);
 * }
 * ```
 */
export function asBackendId(value: string): BackendId {
  return value as BackendId;
}

/**
 * Extract plain string from BackendId (for display/logging)
 *
 * USAGE:
 * ```typescript
 * const id: BackendId = await generateId();
 * console.log(`Created entity with ID: ${toPlainString(id)}`);
 * ```
 *
 * NOTE: This is just for clarity. BackendId extends string,
 * so it can be used anywhere string is expected.
 */
export function toPlainString(id: BackendId): string {
  return id;
}

/**
 * Null-safe BackendId helper
 *
 * USAGE:
 * ```typescript
 * const optionalId: BackendId | null = entity.id;
 * const displayId = backendIdOrDefault(optionalId, 'N/A');
 * ```
 */
export function backendIdOrDefault(id: BackendId | null | undefined, defaultValue: string): string {
  return id ?? defaultValue;
}

/**
 * Compare two BackendIds for equality
 *
 * USAGE:
 * ```typescript
 * if (backendIdEquals(entity.id, selectedId)) {
 *   // IDs match
 * }
 * ```
 *
 * NOTE: Since BackendId extends string, you can use `===` directly
 * This helper is provided for clarity and null-safety
 */
export function backendIdEquals(
  a: BackendId | null | undefined,
  b: BackendId | null | undefined
): boolean {
  if (a === null || a === undefined || b === null || b === undefined) {
    return a === b;
  }
  return a === b;
}

/**
 * Type-safe ID array operations
 */
export function isBackendIdArray(value: unknown): value is BackendId[] {
  return Array.isArray(value) && value.every(isBackendId);
}

export function uniqueBackendIds(ids: BackendId[]): BackendId[] {
  return Array.from(new Set(ids));
}

export function filterNullIds(ids: (BackendId | null | undefined)[]): BackendId[] {
  return ids.filter((id): id is BackendId => id !== null && id !== undefined);
}

/**
 * MIGRATION GUIDE: Gradual Adoption Strategy
 *
 * PHASE 1: Update Type Definitions
 * ```typescript
 * // Before
 * interface Workflow {
 *   id: string;
 * }
 *
 * // After
 * interface Workflow {
 *   id: BackendId;
 * }
 * ```
 *
 * PHASE 2: Update useGenerateId Hook
 * ```typescript
 * // Update return type to BackendId
 * export function useGenerateId(): UseGenerateIdReturn {
 *   const generateId = useCallback(async (): Promise<BackendId> => {
 *     const id = await idGenerationRepository.generateId(type);
 *     return id as BackendId; // Assertion safe (from backend)
 *   }, []);
 * }
 * ```
 *
 * PHASE 3: Update Repository Return Types
 * ```typescript
 * class WorkflowRepository {
 *   async findByTenantAndId(tenant: string, id: BackendId): Promise<Workflow | null> {
 *     // ...
 *   }
 *
 *   async create(tenant: string, data: CreateWorkflowInput): Promise<BackendId> {
 *     // Backend returns Snowflake ID
 *     const response = await mutation();
 *     return response.id as BackendId; // Assertion safe (from backend)
 *   }
 * }
 * ```
 *
 * PHASE 4: Update Component Props
 * ```typescript
 * interface WorkflowEditorProps {
 *   workflowId: BackendId;
 *   onSave: (id: BackendId) => void;
 * }
 * ```
 *
 * PHASE 5: Handle API Boundaries
 * ```typescript
 * // When receiving from API
 * const response = await fetch('/api/workflow');
 * const data = await response.json();
 * if (isBackendId(data.id)) {
 *   const workflow: Workflow = { ...data, id: data.id };
 * }
 *
 * // When sending to API (BackendId extends string, works automatically)
 * await fetch('/api/workflow', {
 *   body: JSON.stringify({ id: workflowId }) // workflowId: BackendId
 * });
 * ```
 *
 * EXPECTED COMPILE ERRORS (These are GOOD - they catch bugs):
 * ```typescript
 * // ❌ ERROR: Can't use arbitrary string
 * const id: BackendId = "abc123"; // Type error
 *
 * // ❌ ERROR: Can't use Date.now()
 * const id: BackendId = `workflow_${Date.now()}`; // Type error
 *
 * // ❌ ERROR: Can't use Math.random()
 * const id: BackendId = Math.random().toString(); // Type error
 *
 * // ✅ FIX: Use useGenerateId() hook
 * const { generateId } = useGenerateId();
 * const id: BackendId = await generateId(EntityType.WORKFLOW);
 *
 * // ✅ FIX: Validate external IDs
 * if (isBackendId(externalId)) {
 *   const id: BackendId = externalId;
 * }
 *
 * // ✅ FIX: Assert backend response (safe)
 * const id = asBackendId(apiResponse.id);
 * ```
 */

/**
 * TESTING GUIDE: How to Test with Branded Types
 *
 * OPTION 1: Use asBackendId() for test fixtures
 * ```typescript
 * const mockWorkflow: Workflow = {
 *   id: asBackendId('mock123456'),
 *   name: 'Test Workflow'
 * };
 * ```
 *
 * OPTION 2: Generate real Snowflake IDs in tests
 * ```typescript
 * const { generateId } = useGenerateId();
 * const id = await generateId(EntityType.WORKFLOW);
 * ```
 *
 * OPTION 3: Mock useGenerateId() hook
 * ```typescript
 * vi.mock('../hooks/useGenerateId', () => ({
 *   useGenerateId: () => ({
 *     generateId: vi.fn().mockResolvedValue(asBackendId('mock123'))
 *   })
 * }));
 * ```
 */
